import { useEffect, useState } from 'react';
import api from '../../services/api';
import Button from '../../components/Button';
import Select from '../../components/Select';
import Input from '../../components/Input';
import Table, { Column } from '../../components/Table';

interface Template {
  id: string;
  name: string;
}
interface PublishRow {
  id: string;
  code: string;
  full_name: string;
  basic_salary: string;
  quantitative_score: string;
  grade_name: string;
  published: boolean;
  recommendation_promotion: boolean;
  recommendation_increment_percent: string;
  recommendation_increment_amount: string;
  recommendation_notes: string;
}

const MONTHS = Array.from({ length: 12 }, (_, i) => ({ value: String(i + 1), label: String(i + 1).padStart(2, '0') }));

export default function PublishAppraisal() {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [templateId, setTemplateId] = useState('');
  const [month, setMonth] = useState(String(new Date().getMonth() + 1));
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [rows, setRows] = useState<PublishRow[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [editing, setEditing] = useState<Record<string, Partial<PublishRow>>>({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    api.get('/pas/masters/templates').then((res) => {
      const list = res.data.templates || [];
      setTemplates(list);
      if (list.length && !templateId) setTemplateId(list[0].id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    if (!templateId || !month || !year) return;
    setLoading(true);
    try {
      const res = await api.get('/pas/publish', { params: { templateId, month, year } });
      setRows(res.data.assignments || []);
      setSelected([]);
      setEditing({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId, month, year]);

  const toggleSelected = (id: string) => setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const editRow = (id: string, field: keyof PublishRow, value: any) => {
    setEditing({ ...editing, [id]: { ...editing[id], [field]: value } });
  };

  const saveEdits = async (row: PublishRow) => {
    const edit = editing[row.id];
    if (!edit) return;
    await api.put(`/pas/assignments/${row.id}/recommendation`, {
      promotion: edit.recommendation_promotion ?? row.recommendation_promotion,
      incrementPercent: edit.recommendation_increment_percent ?? row.recommendation_increment_percent,
      incrementAmount: edit.recommendation_increment_amount ?? row.recommendation_increment_amount,
      notes: edit.recommendation_notes ?? row.recommendation_notes,
    });
    load();
  };

  const publish = async () => {
    if (!selected.length) return;
    setLoading(true);
    try {
      const res = await api.post('/pas/publish', { assignmentIds: selected });
      const skipped = (res.data.results || []).filter((r: any) => r.incrementSkipped).length;
      setMessage(`Published ${res.data.results.length} appraisal(s)${skipped ? ` — ${skipped} increment(s) skipped (payroll already initialized)` : ''}`);
      load();
    } finally {
      setLoading(false);
    }
  };

  const columns: Column<PublishRow>[] = [
    {
      key: 'select',
      header: '',
      render: (r) => <input type="checkbox" disabled={r.published} checked={selected.includes(r.id)} onChange={() => toggleSelected(r.id)} />,
    },
    { key: 'code', header: 'Code' },
    { key: 'full_name', header: 'Name' },
    { key: 'quantitative_score', header: 'Score', render: (r) => (r.quantitative_score != null ? `${r.quantitative_score}%` : '—') },
    { key: 'grade_name', header: 'Grade', render: (r) => r.grade_name || '—' },
    {
      key: 'promotion',
      header: 'Promotion',
      render: (r) => (
        <input
          type="checkbox"
          disabled={r.published}
          checked={editing[r.id]?.recommendation_promotion ?? r.recommendation_promotion}
          onChange={(e) => editRow(r.id, 'recommendation_promotion', e.target.checked)}
        />
      ),
    },
    {
      key: 'incrementPercent',
      header: 'Increment %',
      render: (r) => (
        <input
          className="border rounded px-2 py-1 w-20 text-sm"
          disabled={r.published}
          type="number"
          value={editing[r.id]?.recommendation_increment_percent ?? r.recommendation_increment_percent ?? ''}
          onChange={(e) => editRow(r.id, 'recommendation_increment_percent', e.target.value)}
        />
      ),
    },
    {
      key: 'incrementAmount',
      header: 'Increment Amount',
      render: (r) => (
        <input
          className="border rounded px-2 py-1 w-24 text-sm"
          disabled={r.published}
          type="number"
          value={editing[r.id]?.recommendation_increment_amount ?? r.recommendation_increment_amount ?? ''}
          onChange={(e) => editRow(r.id, 'recommendation_increment_amount', e.target.value)}
        />
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (r) =>
        !r.published && editing[r.id] ? (
          <Button variant="secondary" onClick={() => saveEdits(r)}>Save</Button>
        ) : r.published ? (
          <span className="text-xs text-green-700">Published</span>
        ) : null,
    },
  ];

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">Publish Appraisal</h1>
      {message && <div className="mb-4 text-sm text-green-700 bg-green-50 border border-green-200 rounded px-4 py-2">{message}</div>}

      <div className="bg-white rounded-lg shadow p-4 mb-4 grid grid-cols-3 gap-4">
        <Select label="Template" value={templateId} onChange={(e) => setTemplateId(e.target.value)} options={templates.map((t) => ({ value: t.id, label: t.name }))} />
        <Select label="Month" value={month} onChange={(e) => setMonth(e.target.value)} options={MONTHS} />
        <Input label="Year" type="number" value={year} onChange={(e) => setYear(e.target.value)} />
      </div>

      <div className="mb-3 flex justify-between items-center">
        <span className="text-sm text-gray-500">Only appraisals that have completed the full approval chain are shown. Publishing is irreversible.</span>
        <Button onClick={publish} disabled={!selected.length}>Publish Selected ({selected.length})</Button>
      </div>

      <Table columns={columns} rows={rows} loading={loading} emptyMessage="No completed appraisals for this template/period" />
    </div>
  );
}
